import { useReveal } from "../lib/useReveal";
import { HeartHandshake, ShieldCheck, Sparkles, Users, BookOpen, MessageCircle } from "lucide-react";

const PRINCIPLES = [
    {
        Icon: HeartHandshake,
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-700",
        title: "Warm, human-centered support",
        body: "Genesis is designed to feel welcoming, calm, and compassionate, especially on the hardest days.",
    },
    {
        Icon: ShieldCheck,
        iconBg: "bg-blue-100",
        iconColor: "text-blue-700",
        title: "Privacy-first by design",
        body: "No public profiles, no social feed, and no selling your recovery story to advertisers or insurers.",
    },
    {
        Icon: Sparkles,
        iconBg: "bg-purple-100",
        iconColor: "text-purple-700",
        title: "AI support that meets you where you are",
        body: "Talk to Hope for grounding, reflection, and encouragement whenever you need a steady voice.",
    },
    {
        Icon: Users,
        iconBg: "bg-amber-100",
        iconColor: "text-amber-700",
        title: "Support beyond the screen",
        body: "From mentors to verified treatment options, Genesis can help connect you to real-world care when you're ready.",
    },
];

export default function Audience() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="audience-section"
            id="about"
            className="py-20 md:py-24 bg-background scroll-mt-24"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-start reveal">
                    <div className="lg:sticky lg:top-28">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-5">
                            <BookOpen className="w-4 h-4" />
                            About Genesis Recovery AI
                        </div>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-tight">
                            Built to feel as safe and supportive as a trusted friend.
                        </h2>
                        <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                            Genesis Recovery AI combines private digital support with compassionate,
                            real-world recovery resources. The goal is simple: help more people feel
                            less alone, more supported, and more able to take the next right step.
                        </p>
                        <div className="mt-8 rounded-3xl border bg-muted/40 p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-white shadow-sm border flex items-center justify-center shrink-0">
                                    <MessageCircle className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-foreground">
                                        Keep your strongest points front and center
                                    </div>
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                        Talk to Hope, privacy-first recovery, daily check-ins, private
                                        journaling, mentor support, and the new directory all work
                                        together as one complete recovery platform.
                                    </p>
                                </div>
                            </div>
                        </div>
                </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                        {PRINCIPLES.map((g, i) => (
                            <article
                                key={g.title}
                                data-testid={`audience-card-${i}`}
                                className="bg-card border rounded-3xl p-7 hover:shadow-lg hover:border-primary/30 hover:-translate-y-1 transition-all"
                            >
                                <div
                                    className={`w-14 h-14 ${g.iconBg} rounded-2xl flex items-center justify-center mb-5`}
                                >
                                    <g.Icon className={`w-6 h-6 ${g.iconColor}`} />
                                </div>
                                <h3 className="text-xl font-bold text-foreground mb-3 leading-snug">
                                    {g.title}
                                </h3>
                                <p className="text-muted-foreground leading-relaxed">{g.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
